/* eslint-disable no-param-reassign */
import produce, {castDraft} from 'immer';

import {PredicateOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {PreviewBuilderModel, PreviewerLinkTypesEnum} from '../../../models/api/previewerModel';
import {ClientRuleCondition} from '../../rules/ruleConditionModel';
import {ConditionsActionTypesEnum} from '../conditions/conditionsActions';
import {isExpressionAction} from '../expressions/expressionActions';
import {buildAtoms, TransformerIdsEnum} from '../expressions/expressionInferHelpers';
import {addExpressionReducer, expressionReducer, setPanelOpenReducer} from '../expressions/expressionReducer';
import {createExpressionsContext} from '../expressions/expressionsContext';
import {PreviewerEditorAction, PreviewerEditorActionTypesEnum} from './previewerEditorActions';
import {PreviewerEditorState} from './previewerEditorState';

export function previewerEditorReducer(
  state: PreviewerEditorState,
  action: PreviewerEditorAction,
): PreviewerEditorState {
  if (isExpressionAction(action)) {
    return expressionReducer(state, action, {});
  }

  switch (action.type) {
    case PreviewerEditorActionTypesEnum.UPDATE_LINK_TYPE:
      return updatePreviewerLinkTypeReducer(state, action.linkType);

    case PreviewerEditorActionTypesEnum.UPDATE_DOMAIN:
      return updatePreviewerDomainReducer(state, action.domain);

    case PreviewerEditorActionTypesEnum.UPDATE_DESCRIPTION:
      return updatePreviewerDescriptionReducer(state, action.description);

    case PreviewerEditorActionTypesEnum.UPDATE_EXAMPLE_URL:
      return updatePreviewerExampleUrlReducer(state, action.exampleUrl);

    case PreviewerEditorActionTypesEnum.START_REQUEST_EXPRESSION:
      return startRequestExpressionReducer(state);

    case PreviewerEditorActionTypesEnum.UPDATE_PREVIEW_BUILDER:
      return updatePreviewBuilderReducer(state, action.previewBuilder);

    case ConditionsActionTypesEnum.ADD_CONDITION:
      return addPreviewerConditionReducer(state, action.indexAND, action.condition);

    case ConditionsActionTypesEnum.UPDATE_CONDITION:
      return updatePreviewerConditionReducer(state, action.indexAND, action.predicate, action.update);

    case ConditionsActionTypesEnum.DELETE_CONDITION:
      return deletePreviewerConditionReducer(state, action.indexAND, action.predicate);

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function updatePreviewerLinkTypeReducer(state: PreviewerEditorState, linkType: PreviewerLinkTypesEnum) {
  return produce(state, (draft) => {
    draft.previewerState.linkType = linkType;
  });
}

function updatePreviewerDomainReducer(state: PreviewerEditorState, domain: string) {
  return produce(state, (draft) => {
    draft.previewerState.domain = domain;
  });
}

function updatePreviewerDescriptionReducer(state: PreviewerEditorState, description: string) {
  return produce(state, (draft) => {
    draft.previewerState.description = description;
  });
}

function updatePreviewerExampleUrlReducer(state: PreviewerEditorState, exampleUrl: string | undefined) {
  return produce(state, (draft) => {
    draft.previewerState.exampleUrl = exampleUrl;
    draft.hasDryRunTestData = Boolean(exampleUrl);
  });
}

function startRequestExpressionReducer(state: PreviewerEditorState) {
  const context = createExpressionsContext({
    expressions: state.expressions,
    customFields: state.customFields,
    initialInfer: state.initialInfer,
  });

  const atoms = buildAtoms(
    [
      {
        transform: TransformerIdsEnum.FETCH,
      },
    ],
    context.initialInfer,
    context,
    {},
  );

  return setPanelOpenReducer(addExpressionReducer(state, atoms, {}), true);
}

function updatePreviewBuilderReducer(state: PreviewerEditorState, previewBuilder: PreviewBuilderModel) {
  return produce(state, (draft) => {
    draft.previewerState.previewBuilder = castDraft(previewBuilder);
  });
}

// @NOTE: We'll be able to have the same reducers as in the rule editor when we'll get rid of errors.

function addPreviewerConditionReducer(
  state: PreviewerEditorState,
  indexAND: number,
  condition: ClientRuleCondition,
) {
  return produce(state, (draft) => {
    const newCondition = castDraft(condition);

    if (!draft.previewerState.conditions[indexAND]) {
      // Append an 'AND' condition to the end.
      draft.previewerState.conditions.push([newCondition]);
      return;
    }

    // Append an 'OR' condition to an existing 'AND' node.
    draft.previewerState.conditions[indexAND].push(newCondition);
  });
}

function updatePreviewerConditionReducer(
  state: PreviewerEditorState,
  indexAND: number,
  predicate: PredicateOf<ClientRuleCondition>,
  update: (cond: ClientRuleCondition) => ClientRuleCondition,
) {
  return produce(state, (draft) => {
    const orConditions = draft.previewerState.conditions[indexAND];
    if (!orConditions) {
      return;
    }

    for (let i = 0; i < orConditions.length; i++) {
      if (predicate(orConditions[i])) {
        orConditions[i] = castDraft(update(orConditions[i]));
        break;
      }
    }
  });
}

function deletePreviewerConditionReducer(
  state: PreviewerEditorState,
  indexAND: number,
  predicate: PredicateOf<ClientRuleCondition>,
) {
  return produce(state, (draft) => {
    const orConditions = draft.previewerState.conditions[indexAND];
    if (!orConditions) {
      return;
    }

    // Delete condition in 'OR' list.
    const index = orConditions.findIndex(predicate);

    if (index >= 0) {
      orConditions.splice(index, 1);
    }

    // If 'OR' list is empty, delete 'OR' item in 'AND' list.
    if (orConditions.length === 0) {
      draft.previewerState.conditions.splice(indexAND, 1);
    }
  });
}
